/**
 * §7.2 screen 3 — Detail.
 *
 * Three rules this screen exists to honour:
 *
 * 1. **The phone is click-to-reveal, and revealing is an audited server call.**
 *    The initial load discards `phone` before it reaches component state, so the
 *    number is not sitting in the panel's memory (or a React DevTools tree) for
 *    every appeal a handler merely opens. Pressing reveal re-reads the record,
 *    which is what writes the audit row that evidences the reveal.
 *
 * 2. **No transcoding in v1 (§6.3)**, so the player is offered next to a Download
 *    button and a browser warning outside Chrome/Edge. A handler on Safari must
 *    still be able to do their job.
 *
 * 3. **Lifting a retention hold arms a deletion** (D-032 §2.3 rule 2). Clearing a
 *    hold makes the appeal eligible on the very next nightly run *with its
 *    original expiry* — if that expiry has already passed, it and its media are
 *    deleted that night. The toggle-off confirmation says so explicitly:
 *    "Silently arming a deletion is not acceptable."
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { components } from '@navoiy/api-types';
import type { LegalType, MurojaatStatus, Resolution } from '@navoiy/domain';
import {
  AI_SEVERITY_MAX,
  ANSWER_BODY_MIN_CHARS,
  CLOSING_RESOLUTIONS,
  LEGAL_TYPES,
  LEGAL_TYPE_EXEMPT_RESOLUTION,
  SQL_RISK_MAX,
  SQL_RISK_WEIGHTS,
} from '@navoiy/domain';
import { ApiError, api, errorKey, mediaUrl, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { useI18n } from '../i18n/LocaleContext.jsx';
import { isSupportedMediaBrowser } from '../lib/browser.js';
import { formatBytes, formatDate, formatDateTime, formatDuration } from '../lib/format.js';
import {
  Badge,
  Button,
  Card,
  DataRow,
  ErrorBanner,
  Field,
  Modal,
  Select,
  Spinner,
  TextArea,
  WarningBanner,
} from '../ui/index.jsx';

type FullDetail = components['schemas']['AdminMurojaatDetail'];
/** The record as this screen holds it: never with the plaintext phone. */
type Detail = Omit<FullDetail, 'phone'>;

function stripPhone(record: FullDetail): Detail {
  const { phone: _phone, ...rest } = record;
  return rest;
}

type ActionKind = 'claim' | 'answer' | 'close' | 'extend' | 'reopen';

interface ActionSpec {
  kind: ActionKind;
  labelKey: string;
  to: 'in_review' | 'answered' | 'closed';
  needsResolution?: boolean;
  needsAnswer?: boolean;
  extend?: boolean;
}

/** §2.5 transition table, as the actions a handler is offered. */
function actionsFor(status: MurojaatStatus): ActionSpec[] {
  switch (status) {
    case 'new':
      return [
        { kind: 'claim', labelKey: 'admin.action.claim', to: 'in_review' },
        { kind: 'close', labelKey: 'admin.action.close', to: 'closed', needsResolution: true },
      ];
    case 'in_review':
      return [
        { kind: 'answer', labelKey: 'admin.action.answer', to: 'answered', needsAnswer: true },
        { kind: 'extend', labelKey: 'admin.action.extend', to: 'in_review', extend: true },
        { kind: 'close', labelKey: 'admin.action.close', to: 'closed', needsResolution: true },
      ];
    case 'answered':
      return [{ kind: 'close', labelKey: 'admin.action.close', to: 'closed', needsResolution: true }];
    case 'closed':
      return [{ kind: 'reopen', labelKey: 'admin.action.reopen', to: 'in_review' }];
  }
}

export function DetailScreen(): React.JSX.Element {
  const { murojaatId = '' } = useParams();
  const { t, locale } = useI18n();
  const { allows, expire } = useSession();

  const [detail, setDetail] = useState<Detail | null>(null);
  const [revealedPhone, setRevealedPhone] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState<ActionSpec | null>(null);
  const [holdPrompt, setHoldPrompt] = useState<boolean | null>(null);
  /**
   * True once the server has answered `422 legal_type_required` for the dialog
   * currently open (§14.3.3). It makes the classification control appear
   * INSIDE that dialog rather than replacing it with an error.
   */
  const [legalTypeRequired, setLegalTypeRequired] = useState(false);
  const [savingLegalType, setSavingLegalType] = useState(false);

  const handle = useCallback(
    (caught: unknown) => {
      if (caught instanceof ApiError && caught.status === 401) {
        expire();
        return;
      }
      const { key, values } = errorKey(caught);
      setError(t(key, values));
    },
    [expire, t],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = unwrap(
        await api().GET('/admin/murojaats/{murojaat_id}', {
          // `locale` decides `mahalla_name` only; every other string on this
          // screen is rendered from the panel's own dictionary. It is in the
          // dependency list so a language switch re-reads the record instead of
          // leaving one server-rendered name in the previous language.
          params: { path: { murojaat_id: murojaatId }, query: { locale } },
        }),
      );
      setDetail(stripPhone(data));
    } catch (caught) {
      handle(caught);
    } finally {
      setLoading(false);
    }
  }, [murojaatId, locale, handle]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * The reveal IS the audited read — never done implicitly on mount.
   *
   * `X-Reveal: phone` is what separates it from the load above in `audit_log`.
   * Both are the same GET, so without the header the two reads are one
   * indistinguishable line and the log cannot answer "who unmasked this
   * citizen's number", which is the question the audit exists for.
   */
  async function revealPhone(): Promise<void> {
    try {
      const data = unwrap(
        await api().GET('/admin/murojaats/{murojaat_id}', {
          params: {
            path: { murojaat_id: murojaatId },
            query: { locale },
            header: { 'X-Reveal': 'phone' },
          },
        }),
      );
      setRevealedPhone(data.phone);
      setDetail(stripPhone(data));
    } catch (caught) {
      handle(caught);
    }
  }

  /**
   * §14.3.3 — the transition, optionally carrying the classification.
   *
   * `legalType` is sent only when the dialog collected one, which it does only
   * when the gate would otherwise reject the move. A handler answering an
   * already-classified appeal is never asked to re-confirm a determination that
   * has already been made and audited.
   */
  async function submitAction(
    spec: ActionSpec,
    resolution: string,
    answerBody: string,
    legalType: LegalType | '',
  ): Promise<void> {
    try {
      const data = unwrap(
        await api().PATCH('/admin/murojaats/{murojaat_id}/status', {
          params: { path: { murojaat_id: murojaatId }, query: { locale } },
          body: {
            to: spec.to,
            ...(spec.needsResolution ? { resolution: resolution as Resolution } : {}),
            ...(spec.needsAnswer ? { answer_body: answerBody } : {}),
            ...(spec.extend ? { extend_due: true } : {}),
            ...(legalType ? { legal_type: legalType } : {}),
          },
        }),
      );
      setDetail(stripPhone(data));
      setRevealedPhone(null);
      setAction(null);
      setError(null);
    } catch (caught) {
      /**
       * The one error this screen does NOT show as a banner.
       *
       * `legal_type_required` is not a failure the handler caused; it is the
       * gate telling them a step is missing, and the step is one radio group
       * away. Surfacing it as a red banner over the whole screen — with the
       * dialog closed and the answer they just typed gone — would make a
       * two-second correction feel like an incident. Instead the dialog stays
       * open and grows the classification control, with the prompt inline.
       */
      if (caught instanceof ApiError && caught.fieldCode('legal_type') === 'legal_type_required') {
        setLegalTypeRequired(true);
        return;
      }
      handle(caught);
    }
  }

  /**
   * §14.3.3 — the classification, from either of the two staff controls.
   *
   * `result` distinguishes the radio group (`manual`) from the AI block's
   * one-click accept (`ai_accepted`), and it changes NOTHING about the value
   * written: both go through the same route, both store the same column, both
   * record the same staff member as having made the determination. That is
   * §14.3.1's ruling made concrete — accepting a suggestion is a staff act, and
   * giving it its own endpoint would give the column a second, machine-shaped
   * write path, which is exactly what the ruling exists to prevent. The marker
   * lives in the audit row so a later reader can ask how many classifications
   * were confirmations rather than judgements.
   */
  async function saveLegalType(
    next: LegalType,
    result: 'manual' | 'ai_accepted' = 'manual',
  ): Promise<void> {
    setSavingLegalType(true);
    try {
      const data = unwrap(
        await api().PATCH('/admin/murojaats/{murojaat_id}/legal-type', {
          params: { path: { murojaat_id: murojaatId }, query: { locale } },
          body: { legal_type: next, result },
        }),
      );
      setDetail(stripPhone(data));
      setRevealedPhone(null);
      setError(null);
    } catch (caught) {
      handle(caught);
    } finally {
      setSavingLegalType(false);
    }
  }

  async function setHold(hold: boolean): Promise<void> {
    try {
      const data = unwrap(
        await api().PATCH('/admin/murojaats/{murojaat_id}/retention-hold', {
          params: { path: { murojaat_id: murojaatId }, query: { locale } },
          body: { hold },
        }),
      );
      setDetail(stripPhone(data));
      setRevealedPhone(null);
      setHoldPrompt(null);
      setError(null);
    } catch (caught) {
      handle(caught);
    }
  }

  if (loading && !detail) return <Spinner label={t('admin.common.loading')} />;
  if (!detail) return <ErrorBanner message={error ?? t('admin.error.notFound')} />;

  const canRead = allows('appeal.read');
  const canTransition = allows('appeal.transition');
  const canHold = allows('appeal.retention_hold');
  const canReopen = allows('appeal.reopen');
  const canClassify = allows('appeal.classify');
  // §14.1 — a separate capability from `appeal.read`, so a deployment can let
  // staff read appeals without machine opinions on them.
  const canSeeAi = allows('ai.read');
  const available = actionsFor(detail.status).filter((spec) =>
    spec.kind === 'reopen' ? canReopen : canTransition,
  );
  // What the stored bytes ARE, which since the 2026-09-27 attachment ruling is
  // no longer implied by `kind`. Read from the mime and nowhere else.
  const mediaMime = detail.media?.mime ?? '';
  const isImage = mediaMime.startsWith('image/');
  const isVideo = mediaMime.startsWith('video/');

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/murojaats" className="text-sm font-semibold text-brand-700 hover:underline">
          ← {t('admin.common.back')}
        </Link>
        <h1 className="text-xl font-bold tracking-ref text-brand-900">{detail.reference}</h1>
        <Badge tone={detail.status === 'closed' ? 'neutral' : 'brand'}>
          {t(`shared.murojaat.status.${detail.status}`)}
        </Badge>
        {detail.retention_hold ? <Badge tone="danger">{t('admin.detail.retentionHoldOn')}</Badge> : null}
      </div>

      <ErrorBanner message={error} />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[3fr_2fr]">
        <div className="flex flex-col gap-5">
          {/* "Yozuv" is the right word for a recording and the wrong one for a
              photo, so the heading follows the mime like the element below it
              does. */}
          <Card title={isImage ? t('admin.detail.mediaPhoto') : t('admin.detail.media')}>
            {/* A destroyed recording is a TOMBSTONE, not a broken player
                (D-032 §2.3, RULING B-1). Offering controls that can only ever
                produce a 404 makes the panel look broken and invites a handler
                to keep retrying; the notice says what happened and when, which
                is also what a prosecutor asks. */}
            {detail.media?.destroyed_at ? (
              <WarningBanner>
                {t('admin.detail.mediaDestroyed', {
                  date: formatDateTime(detail.media.destroyed_at),
                })}
              </WarningBanner>
            ) : detail.media ? (
              <div className="flex flex-col gap-3">
                {/* The browser warning is about CODECS, so it belongs to the
                    two players and not to a photo: a JPEG renders in Safari
                    exactly as it does in Chrome, and telling a handler to
                    switch browsers to look at a picture is a warning that
                    trains them to ignore warnings. */}
                {!isImage && !isSupportedMediaBrowser() ? (
                  <WarningBanner>{t('admin.detail.browserWarning')}</WarningBanner>
                ) : null}

                {/*
                  WHICH ELEMENT IS DECIDED BY THE STORED MIME, NEVER BY `kind`.

                  Since the 2026-09-27 owner ruling a `kind: 'text'` appeal may
                  carry a photo or a short video in the same single `media_id`
                  slot, so `kind` no longer tells you what the bytes are —
                  `detail.media.mime` does, and it is the mime of what was
                  actually stored (a photo is re-encoded to JPEG on ingest).
                  Reading `kind` here would have rendered a citizen's photo into
                  an `<audio>` element: no error, no player, a handler told the
                  appeal has an attachment and shown nothing.

                  No `<img>` lightbox and no zoom. The photo is rendered at the
                  panel's width with the full-size object one click away under
                  Download — the same shape the players already have, and it
                  keeps every read of the bytes going through the one audited
                  media endpoint.
                */}
                {isImage ? (
                  <img
                    src={mediaUrl(detail.id)}
                    alt={t('admin.detail.mediaPhoto')}
                    className="w-full rounded-tile bg-surface-100 object-contain"
                  />
                ) : isVideo ? (
                  <video
                    controls
                    preload="none"
                    className="w-full rounded-tile bg-brand-900"
                    src={mediaUrl(detail.id)}
                  />
                ) : (
                  <audio controls preload="none" className="w-full" src={mediaUrl(detail.id)} />
                )}

                <div className="flex flex-wrap items-center gap-3">
                  <a
                    href={mediaUrl(detail.id)}
                    download={`${detail.reference}`}
                    className="inline-flex items-center rounded-btn border-1 border-line-300 bg-surface-0 px-4 py-2 text-sm font-semibold text-brand-800 hover:bg-surface-100"
                  >
                    {t('admin.detail.download')}
                  </a>
                  <span className="text-xs text-ink-500">
                    {detail.media.mime} · {formatBytes(detail.media.bytes)} ·{' '}
                    {formatDuration(detail.media.duration_ms_reported)}
                  </span>
                  {detail.media.scan_status !== 'clean' ? (
                    <Badge tone={detail.media.scan_status === 'infected' ? 'danger' : 'gold'}>
                      {t('admin.detail.scanStatus')}: {t(`admin.scan.${detail.media.scan_status}`)}
                    </Badge>
                  ) : null}
                </div>

                {/* §8.3 — the identification mode is what tells a handler whether
                    the recording can satisfy Art. 6 at all. It belongs beside the
                    player, not buried in a metadata list. */}
                <p className="rounded-chip bg-surface-50 px-3 py-2 text-sm text-ink-700">
                  <span className="font-semibold">{t('admin.detail.identification')}: </span>
                  {t(`admin.identification.${detail.identification_mode}`)}
                </p>
              </div>
            ) : (
              <p className="text-sm text-ink-500">{t('admin.detail.mediaMissing')}</p>
            )}
          </Card>

          {/* Migration 0017 — beside the player, because that is the thing it
              is a stand-in for. Gated on `appeal.read` like the body: a
              transcript is the citizen's spoken appeal as text, derived from
              biometric data (F-9), and is exactly as sensitive as
              `author_name`. A `viewer` sees the player and not this. */}
          {/* A text appeal has no transcript to show — unless it attached a
              video, which the worker transcribes exactly like a video appeal's
              recording (owner ruling 2026-09-27). `transcript_status` is the
              honest test: it is `none` for a plain text appeal and moves off it
              only when there was something audible to read. */}
          {canRead && (detail.kind !== 'text' || detail.transcript_status !== 'none') ? (
            <TranscriptPanel
              status={detail.transcript_status}
              text={detail.transcript_text}
              model={detail.transcript_model}
            />
          ) : null}

          {/*
            §14.4 — the AI block, gated on `ai.read` AND on `appeal.read`.
            Placed after the body rather than before it, deliberately: a
            handler should read what the citizen wrote before they read a
            machine's opinion of it.
          */}
          {canRead && canSeeAi && detail.ai ? (
            <AiPanel
              ai={detail.ai}
              currentLegalType={detail.legal_type}
              canClassify={canClassify}
              saving={savingLegalType}
              onAccept={(next) => void saveLegalType(next, 'ai_accepted')}
            />
          ) : null}

          {canRead ? (
            <Card title={t('admin.detail.body')}>
              <dl className="flex flex-col">
                <DataRow label={t('admin.detail.authorName')}>{detail.author_name ?? '—'}</DataRow>
                <DataRow label={t('admin.detail.authorAddress')}>
                  {detail.author_address ?? '—'}
                </DataRow>
                <DataRow label={t('admin.detail.body')}>
                  {detail.body ? (
                    <p className="whitespace-pre-wrap">{detail.body}</p>
                  ) : (
                    '—'
                  )}
                </DataRow>
                <DataRow label={t('admin.detail.answer')}>
                  {detail.answer_body ? (
                    <p className="whitespace-pre-wrap">{detail.answer_body}</p>
                  ) : (
                    '—'
                  )}
                </DataRow>
              </dl>
            </Card>
          ) : null}

          <Card title={t('admin.detail.history')}>
            {detail.history.length === 0 ? (
              <p className="text-sm text-ink-500">{t('admin.detail.noHistory')}</p>
            ) : (
              <ol className="flex flex-col gap-3">
                {detail.history.map((entry, index) => (
                  <li
                    key={`${entry.at}-${index}`}
                    className="grid grid-cols-[10rem_1fr] gap-3 border-l-3 border-line-200 pl-3 text-sm"
                  >
                    <span className="text-xs text-ink-500">{formatDateTime(entry.at)}</span>
                    <span className="text-ink-900">
                      <span className="font-semibold">{entry.action}</span>
                      {entry.from_status && entry.to_status ? (
                        <span className="text-ink-500">
                          {' '}
                          {t(`shared.murojaat.status.${entry.from_status}`)} →{' '}
                          {t(`shared.murojaat.status.${entry.to_status}`)}
                        </span>
                      ) : null}
                      {entry.actor_name ? (
                        <span className="text-ink-500"> · {entry.actor_name}</span>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </div>

        <div className="flex flex-col gap-5">
          <Card title={t('admin.column.status')}>
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap gap-2">
                {available.map((spec) => (
                  <Button key={spec.kind} tone="secondary" onClick={() => setAction(spec)}>
                    {t(spec.labelKey)}
                  </Button>
                ))}
              </div>
              {available.length === 0 ? (
                <p className="text-xs text-ink-500">{t('admin.error.forbidden')}</p>
              ) : null}
            </div>
          </Card>

          <Card title={t('admin.detail.title', { reference: detail.reference })}>
            <dl className="flex flex-col">
              <DataRow label={t('admin.column.mahalla')}>{detail.mahalla_name}</DataRow>
              <DataRow label={t('admin.column.kind')}>
                {t(`shared.murojaat.kind.${detail.kind}`)}
              </DataRow>
              <DataRow label={t('admin.column.channel')}>
                {t(`admin.channel.${detail.channel}`)}
              </DataRow>

              {/*
                §14.5 — WHO TYPED IT, present only for a walk-in.
                It sits directly under the channel because the two facts belong
                together: `reception` says a person came to the building, and
                this says which staff member took the appeal down. It is
                explicitly NOT the assignee — filing is not handling — and the
                label says so, because a reader who takes it for the assignee
                will chase the wrong person for an answer.
              */}
              {detail.filed_by_name ? (
                <DataRow label={t('admin.detail.filedBy')}>
                  <span className="flex flex-col">
                    <span>{detail.filed_by_name}</span>
                    <span className="text-xs text-ink-500">{t('admin.detail.filedByNote')}</span>
                  </span>
                </DataRow>
              ) : null}

              {/* Click-to-reveal. Masked by default; the tail is the only part a
                  citizen is ever shown, so it is safe to display unprompted. */}
              <DataRow label={t('admin.detail.revealPhone')}>
                {canRead ? (
                  revealedPhone ? (
                    <span className="font-semibold tracking-ref">{revealedPhone}</span>
                  ) : (
                    <span className="flex flex-col items-start gap-1">
                      <span className="text-ink-500">
                        {t('admin.detail.phoneHidden', { tail: detail.phone_tail })}
                      </span>
                      <Button tone="ghost" onClick={() => void revealPhone()}>
                        {t('admin.detail.revealPhone')}
                      </Button>
                      <span className="text-xs text-ink-400">
                        {t('admin.detail.phoneRevealNote')}
                      </span>
                    </span>
                  )
                ) : (
                  '—'
                )}
              </DataRow>

              <DataRow label={t('admin.detail.addressedBody')}>{detail.addressed_body}</DataRow>
              <DataRow label={t('admin.detail.submittedAt')}>
                {formatDateTime(detail.submitted_at)}
              </DataRow>
              <DataRow label={t('admin.column.received')}>
                {formatDateTime(detail.received_at)}
              </DataRow>
              <DataRow label={t('admin.column.due')}>{formatDate(detail.due_at)}</DataRow>
              {detail.extended_due_at ? (
                <DataRow label={t('admin.detail.dueExtended')}>
                  {formatDate(detail.extended_due_at)}
                </DataRow>
              ) : null}
              {detail.resolution ? (
                <DataRow label={t('admin.detail.resolutionLabel')}>
                  {t(`admin.resolution.${detail.resolution}`)}
                </DataRow>
              ) : null}
              {detail.client_code ? (
                <DataRow label={t('admin.detail.clientCode')}>
                  <span className="tracking-ref">{detail.client_code}</span>
                </DataRow>
              ) : null}
              {detail.queued_ms ? (
                <DataRow label={t('admin.detail.queuedMs')}>
                  {formatDuration(detail.queued_ms)}
                </DataRow>
              ) : null}
              <DataRow label={t('admin.detail.consent')}>{detail.consent_version}</DataRow>
            </dl>
          </Card>

          {/*
            §14.3 — the classification control.
            Placed in the sidebar next to the lifecycle actions rather than
            beside the body, because it IS a lifecycle fact: the gate on
            `answered`/`closed` is what makes it one.
          */}
          {canClassify ? (
            <Card title={t('admin.detail.legalTypeTitle')}>
              <LegalTypeControl
                value={detail.legal_type}
                setBy={detail.legal_type_set_by_name}
                setAt={detail.legal_type_set_at}
                saving={savingLegalType}
                onSelect={(next) => void saveLegalType(next)}
              />
            </Card>
          ) : detail.legal_type ? (
            // A reader without `appeal.classify` still sees WHAT it is; they
            // simply cannot change it. Hiding the determination from them would
            // hide a fact of the case, not a control.
            <Card title={t('admin.detail.legalTypeTitle')}>
              <p className="text-sm font-semibold text-ink-900">
                {t(`admin.legalType.${detail.legal_type}`)}
              </p>
            </Card>
          ) : null}

          {canHold ? (
            <Card title={t('admin.detail.retentionHold')}>
              <div className="flex flex-col gap-3">
                <p className="text-sm text-ink-700">
                  {detail.retention_hold
                    ? t('admin.detail.retentionHoldOn')
                    : t('admin.detail.retentionHoldOff')}
                </p>
                <Button
                  tone={detail.retention_hold ? 'danger' : 'secondary'}
                  onClick={() => setHoldPrompt(!detail.retention_hold)}
                >
                  {detail.retention_hold
                    ? t('admin.detail.retentionHoldLift')
                    : t('admin.detail.retentionHoldPlace')}
                </Button>
              </div>
            </Card>
          ) : null}
        </div>
      </div>

      {action ? (
        <ActionDialog
          spec={action}
          currentLegalType={detail.legal_type}
          legalTypeRequired={legalTypeRequired}
          canClassify={canClassify}
          onClose={() => {
            setAction(null);
            setLegalTypeRequired(false);
          }}
          onSubmit={(resolution, answerBody, legalType) =>
            void submitAction(action, resolution, answerBody, legalType)
          }
        />
      ) : null}

      {holdPrompt !== null ? (
        <Modal
          title={
            holdPrompt ? t('admin.detail.retentionHoldPlace') : t('admin.detail.retentionHoldLift')
          }
          onClose={() => setHoldPrompt(null)}
          footer={
            <>
              <Button tone="secondary" onClick={() => setHoldPrompt(null)}>
                {t('admin.common.cancel')}
              </Button>
              <Button tone={holdPrompt ? 'primary' : 'danger'} onClick={() => void setHold(holdPrompt)}>
                {t('admin.common.confirm')}
              </Button>
            </>
          }
        >
          {/* D-032 §2.3 rule 2 — lifting a hold arms a deletion, and the panel
              must say so. Never silently. */}
          {holdPrompt ? null : <WarningBanner>{t('admin.detail.retentionHoldLiftWarning')}</WarningBanner>}
        </Modal>
      ) : null}
    </div>
  );
}

/**
 * The AI transcript panel (migration 0017).
 *
 * THE DISCLAIMER IS NOT DECORATION AND IS NOT CONDITIONAL. A handler reading
 * fluent text next to a player will treat it as a record of what the citizen
 * said; whisper's Uzbek in particular will produce confident, wrong sentences.
 * So the disclaimer renders ABOVE the text, always, in the same block — never
 * collapsed, never behind a tooltip, never only on first view. It is the one
 * element here that has no state in which it disappears.
 *
 * `whitespace-pre-wrap` because the transcript's own line breaks are the only
 * structure it has; collapsing them turns a segmented recording into a wall.
 */
function TranscriptPanel({
  status,
  text,
  model,
}: {
  status: FullDetail['transcript_status'];
  text: string | undefined;
  model: string | undefined;
}): React.JSX.Element {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  async function copy(): Promise<void> {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // A panel served over plain http, or a browser that refuses the
      // clipboard, still has to render. The text is selectable either way.
    }
  }

  return (
    <Card title={t('admin.transcript.title')}>
      {status === 'done' && text !== undefined ? (
        <div className="flex flex-col gap-3">
          <WarningBanner>{t('admin.transcript.disclaimer')}</WarningBanner>
          <p className="whitespace-pre-wrap text-sm text-ink-900">{text}</p>
          <div className="flex flex-wrap items-center gap-3">
            <Button tone="secondary" onClick={() => void copy()}>
              {copied ? t('admin.transcript.copied') : t('admin.transcript.copy')}
            </Button>
            {/* Which model produced this text. A handler who has just read a
                garbled Uzbek paragraph can say WHICH model garbled it, which is
                what makes the quality attributable rather than folkloric. */}
            {model ? (
              <span className="text-xs text-ink-500">
                {t('admin.transcript.model')} {model}
              </span>
            ) : null}
          </div>
        </div>
      ) : (
        <p className="text-sm text-ink-500">
          {status === 'pending'
            ? t('admin.transcript.pending')
            : status === 'processing'
              ? t('admin.transcript.processing')
              : status === 'failed'
                ? t('admin.transcript.failed')
                : /* `none` and `disabled` are one message for a handler: no
                     transcript exists and none is coming. The database keeps
                     them apart for ops — "never enabled" vs "destroyed under
                     retention" — but that distinction is not this reader's
                     problem. */
                  t('admin.transcript.disabled')}
        </p>
      )}
    </Card>
  );
}

/**
 * §14.4 — the per-appeal AI block.
 *
 * ===========================================================================
 * FOUR OF §14.4.5'S SIX HONESTY RULES LIVE IN THIS COMPONENT.
 * ===========================================================================
 *
 * 1. THE DISCLAIMER IS UNCONDITIONAL (rule 1). It renders above everything
 *    else, in every state including `disabled`, in the same block. There is no
 *    branch in which it disappears — the `admin.transcript.disclaimer`
 *    precedent, which already ships one screen up.
 *
 * 2. THE SCORE IS NEVER A BARE NUMBER (rule 4). The SQL components and the AI
 *    severity are separate, separately-labelled bars against their OWN maxima:
 *    0–60 and 0–40. There is no sum anywhere on this screen, and the API sends
 *    no field carrying one. Drawing the severity against a 100-point axis would
 *    render the model's ceiling as "medium" and quietly understate the one
 *    component a reader should discount.
 *
 * 3. `disabled` IS A SENTENCE, NOT AN EMPTY BLOCK (rule 5). "AI tahlil bu
 *    muhitda oʻchirilgan" — because an empty panel reads as "no risk found",
 *    which is a different and false statement.
 *
 * 4. THE SUGGESTION IS NOT A DECISION (rule 2, and §14.3.1). Accepting it is a
 *    button a person presses, it goes through the same `PATCH …/legal-type` a
 *    manual determination uses, and it is stored as staff-set. Nothing in this
 *    component writes anything by itself.
 */
function AiPanel({
  ai,
  currentLegalType,
  canClassify,
  saving,
  onAccept,
}: {
  ai: NonNullable<FullDetail['ai']>;
  currentLegalType: LegalType | undefined;
  canClassify: boolean;
  saving: boolean;
  onAccept: (next: LegalType) => void;
}): React.JSX.Element {
  const { t } = useI18n();
  const done = ai.status === 'done';

  return (
    <Card title={t('admin.ai.title')}>
      <div className="flex flex-col gap-4">
        {/* Rule 1. Above everything, always, in every state. */}
        <WarningBanner>{t('admin.ai.unverified')}</WarningBanner>

        {/*
          The SQL half renders in EVERY state, including `disabled`, because it
          never needed a provider: it is deadline arithmetic over columns the
          database already holds. A deployment with `ANALYZE_PROVIDER=off` still
          gets a defensible risk breakdown, and labelling it as SQL is what
          stops a reader crediting it to a model that is not running.
        */}
        {ai.sql_risk ? (
          <div className="flex flex-col gap-2">
            <p className="text-xs font-semibold uppercase tracking-label text-ink-500">
              {t('admin.ai.riskTitle')}
            </p>
            <p className="text-xs text-ink-500">{t('admin.ai.sqlBasis')}</p>
            <ScoreBar
              label={t('admin.ai.riskSqlLabel')}
              value={ai.sql_risk.total}
              max={SQL_RISK_MAX}
              tone="bg-brand-600"
            />
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-ink-600">
              <Component label={t('admin.ai.risk.deadline')} value={ai.sql_risk.deadline} max={SQL_RISK_WEIGHTS.deadline} />
              <Component label={t('admin.ai.risk.repeat')} value={ai.sql_risk.repeat} max={SQL_RISK_WEIGHTS.repeat} />
              <Component label={t('admin.ai.risk.statusAge')} value={ai.sql_risk.status_age} max={SQL_RISK_WEIGHTS.statusAge} />
              <Component label={t('admin.ai.risk.backlog')} value={ai.sql_risk.backlog} max={SQL_RISK_WEIGHTS.backlog} />
            </dl>
          </div>
        ) : null}

        {done ? (
          <>
            {/* The model's half, on its OWN scale and labelled as the model's. */}
            {ai.severity !== undefined ? (
              <div className="flex flex-col gap-2">
                <p className="text-xs font-semibold uppercase tracking-label text-ink-500">
                  {t('admin.ai.severityTitle')}
                </p>
                <ScoreBar
                  label={t('admin.ai.severityLabel')}
                  value={ai.severity}
                  max={AI_SEVERITY_MAX}
                  tone="bg-gold-500"
                />
                {/* The flags ARE the basis for the number. Rule 4 again: the
                    score never travels alone. */}
                {ai.severity_flags && ai.severity_flags.length > 0 ? (
                  <ul className="flex flex-wrap gap-2">
                    {ai.severity_flags.map((flag) => (
                      <li key={flag}>
                        <Badge tone="gold">{t(`admin.ai.flag.${flag}`)}</Badge>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-ink-500">{t('admin.ai.noFlags')}</p>
                )}
              </div>
            ) : null}

            {ai.topics && ai.topics.length > 0 ? (
              <div className="flex flex-col gap-2">
                <p className="text-xs font-semibold uppercase tracking-label text-ink-500">
                  {t('admin.ai.topicsTitle')}
                </p>
                <ul className="flex flex-wrap gap-2">
                  {ai.topics.map((topic) => (
                    <li key={topic.code}>
                      <Badge tone="brand">
                        {t(`admin.ai.topic.${topic.code}`)} ·{' '}
                        {Math.round(topic.confidence * 100)}%
                      </Badge>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {/*
              THE SUGGESTION, AND THE ONE BUTTON IN THIS COMPONENT.
              Hidden once the appeal is classified: re-offering a suggestion
              beside a determination that has already been made and audited
              invites a reader to treat the model as a second opinion on a
              closed question. It is also hidden from a session without
              `appeal.classify`, which may see the suggestion but may not act
              on it.
            */}
            {ai.legal_type_suggested && currentLegalType === undefined ? (
              <div className="flex flex-col gap-2 rounded-chip border-1 border-line-200 px-3 py-3">
                <p className="text-xs font-semibold uppercase tracking-label text-ink-500">
                  {t('admin.ai.suggestionTitle')}
                </p>
                <p className="text-sm text-ink-900">
                  <span className="font-semibold">
                    {t(`admin.legalType.${ai.legal_type_suggested}`)}
                  </span>
                  {ai.legal_type_confidence !== undefined ? (
                    <span className="text-ink-500">
                      {' '}
                      · {Math.round(ai.legal_type_confidence * 100)}%
                    </span>
                  ) : null}
                </p>
                <p className="text-xs text-ink-500">{t('admin.ai.suggestionNote')}</p>
                {canClassify ? (
                  <Button
                    tone="secondary"
                    disabled={saving}
                    onClick={() => onAccept(ai.legal_type_suggested as LegalType)}
                  >
                    {t('admin.ai.suggestionAccept')}
                  </Button>
                ) : null}
              </div>
            ) : null}

            {/* Which model, under which prompt, and when — the facts that make
                the quality attributable rather than folkloric, and the ones
                that survive a retention destroy. */}
            <p className="text-xs text-ink-400">
              {t('admin.ai.provenance', {
                model: ai.model ?? '—',
                version: ai.prompt_version ?? '—',
                at: formatDateTime(ai.analyzed_at),
              })}
            </p>
          </>
        ) : (
          // Rule 5 — a sentence for every non-`done` state. `pending` and
          // `processing` are honest waits; `failed` says the appeal is
          // unaffected; `disabled` says the feature is off HERE rather than
          // leaving a blank that reads as "nothing found".
          <p className="text-sm text-ink-500">
            {ai.status === 'pending'
              ? t('admin.ai.statusPending')
              : ai.status === 'processing'
                ? t('admin.ai.statusProcessing')
                : ai.status === 'failed'
                  ? t('admin.ai.statusFailed')
                  : t('admin.ai.disabled')}
          </p>
        )}
      </div>
    </Card>
  );
}

/** One labelled bar against its OWN maximum, with the denominator visible. */
function ScoreBar({
  label,
  value,
  max,
  tone,
}: {
  label: string;
  value: number;
  max: number;
  tone: string;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm text-ink-700">{label}</span>
        {/* `n / max`, never `n` alone. The denominator is what stops a reader
            taking a 34 for a percentage or for a score out of a hundred. */}
        <span className="text-sm font-semibold tabular-nums text-ink-900">
          {value} / {max}
        </span>
      </div>
      <span className="h-2 rounded-chip bg-surface-100">
        <span
          className={`block h-2 rounded-chip ${tone}`}
          style={{ width: `${String((value / max) * 100)}%` }}
        />
      </span>
    </div>
  );
}

function Component({
  label,
  value,
  max,
}: {
  label: string;
  value: number;
  max: number;
}): React.JSX.Element {
  return (
    <div className="flex justify-between gap-2">
      <dt>{label}</dt>
      <dd className="tabular-nums font-medium text-ink-800">
        {value} / {max}
      </dd>
    </div>
  );
}

/**
 * §14.3 — the classification radio group.
 *
 * RADIOS, not a `<select>`: there are exactly three options, they are a legal
 * determination rather than a setting, and a closed dropdown hides two thirds
 * of a choice a staff member is making on the record. Each option is a full-row
 * label, so the whole row is the hit target rather than a 16-pixel circle.
 *
 * There is deliberately no "clear" or "unclassify" action. Once a determination
 * has been made and audited it can be CORRECTED — by picking a different one,
 * which writes its own audit row — but returning an appeal to unclassified
 * would erase a fact from the record rather than superseding it.
 *
 * The per-option hints live under `admin.legalTypeHint.*`, a SIBLING namespace
 * rather than `admin.legalType.<type>.hint`. `t()` walks the nested dictionary
 * by splitting on `.`, so the latter would need `ariza` to be a string (the
 * label) and an object (carrying `hint`) at once — not expressible in JSON, and
 * the loser would silently render as its own key. Same reason the audit screen
 * flattens its action keys.
 */
function LegalTypeControl({
  value,
  setBy,
  setAt,
  saving,
  onSelect,
}: {
  value: LegalType | undefined;
  setBy?: string;
  setAt?: string;
  saving: boolean;
  onSelect: (next: LegalType) => void;
}): React.JSX.Element {
  const { t } = useI18n();
  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-ink-500">{t('admin.detail.legalTypeHelp')}</p>
      <div className="flex flex-col gap-1">
        {LEGAL_TYPES.map((option) => (
          <label
            key={option}
            className="flex cursor-pointer items-start gap-2 rounded-chip px-2 py-2 text-sm text-ink-900 hover:bg-surface-50"
          >
            <input
              type="radio"
              name="legal-type"
              value={option}
              checked={value === option}
              disabled={saving}
              onChange={() => onSelect(option)}
              className="mt-0.5 h-4 w-4 accent-brand-600"
            />
            <span className="flex flex-col">
              <span className="font-semibold">{t(`admin.legalType.${option}`)}</span>
              <span className="text-xs text-ink-500">{t(`admin.legalTypeHint.${option}`)}</span>
            </span>
          </label>
        ))}
      </div>

      {value === undefined ? (
        <p className="text-xs font-medium text-gold-700">{t('admin.detail.legalTypeUnset')}</p>
      ) : (
        // Provenance on screen, because §14.3.2 stores it precisely so a reader
        // can be told who made this determination and when.
        <p className="text-xs text-ink-500">
          {t('admin.detail.legalTypeSetBy', {
            name: setBy ?? '—',
            at: formatDateTime(setAt),
          })}
        </p>
      )}
    </div>
  );
}

function ActionDialog({
  spec,
  currentLegalType,
  legalTypeRequired,
  canClassify,
  onClose,
  onSubmit,
}: {
  spec: ActionSpec;
  currentLegalType: LegalType | undefined;
  legalTypeRequired: boolean;
  canClassify: boolean;
  onClose: () => void;
  onSubmit: (resolution: string, answerBody: string, legalType: LegalType | '') => void;
}): React.JSX.Element {
  const { t } = useI18n();
  const [resolution, setResolution] = useState<string>(CLOSING_RESOLUTIONS[0]);
  const [answerBody, setAnswerBody] = useState('');
  const [legalType, setLegalType] = useState<LegalType | ''>('');

  /**
   * §14.3.3 — whether this move needs a classification, computed CLIENT-SIDE so
   * the control appears before the server has to refuse anything.
   *
   * It mirrors the server's rule exactly: `answered` or `closed`, no type
   * already stored, and the resolution is not `left_without_review` — an appeal
   * that was never reviewed (F-5) is never classified. The server enforces the
   * same gate independently; this only decides what the dialog shows, and
   * `legalTypeRequired` is the fallback for any case where the two disagree.
   */
  const gateApplies =
    (spec.to === 'answered' || spec.to === 'closed') &&
    currentLegalType === undefined &&
    resolution !== LEGAL_TYPE_EXEMPT_RESOLUTION;
  const needsLegalType = (gateApplies || legalTypeRequired) && canClassify;

  const ready =
    (spec.needsAnswer ? answerBody.trim().length >= ANSWER_BODY_MIN_CHARS : true) &&
    (needsLegalType ? legalType !== '' : true);

  return (
    <Modal
      title={t(spec.labelKey)}
      onClose={onClose}
      footer={
        <>
          <Button tone="secondary" onClick={onClose}>
            {t('admin.common.cancel')}
          </Button>
          <Button disabled={!ready} onClick={() => onSubmit(resolution, answerBody, legalType)}>
            {t('admin.common.confirm')}
          </Button>
        </>
      }
    >
      {spec.extend ? <WarningBanner>{t('admin.detail.confirmExtend')}</WarningBanner> : null}

      {spec.needsResolution ? (
        <Field label={t('admin.detail.resolutionLabel')}>
          <Select value={resolution} onChange={(event) => setResolution(event.target.value)}>
            {CLOSING_RESOLUTIONS.map((value) => (
              <option key={value} value={value}>
                {t(`admin.resolution.${value}`)}
              </option>
            ))}
          </Select>
        </Field>
      ) : null}

      {/*
        The inline prompt. It renders BEFORE the server refuses when the gate is
        predictable, and after a `422 legal_type_required` when it was not — the
        dialog is never replaced by an error banner, and the answer already
        typed is never lost.
      */}
      {needsLegalType ? (
        <Field label={t('admin.detail.legalTypeTitle')} hint={t('admin.detail.legalTypeRequired')}>
          <div className="flex flex-col gap-1">
            {LEGAL_TYPES.map((option) => (
              <label
                key={option}
                className="flex cursor-pointer items-start gap-2 rounded-chip px-2 py-2 text-sm text-ink-900 hover:bg-surface-50"
              >
                <input
                  type="radio"
                  name="action-legal-type"
                  value={option}
                  checked={legalType === option}
                  onChange={() => setLegalType(option)}
                  className="mt-0.5 h-4 w-4 accent-brand-600"
                />
                <span className="flex flex-col">
                  <span className="font-semibold">{t(`admin.legalType.${option}`)}</span>
                  <span className="text-xs text-ink-500">
                    {t(`admin.legalTypeHint.${option}`)}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </Field>
      ) : null}

      {/* The F-5 exemption, said out loud when it applies — otherwise a handler
          wonders why the classification prompt vanished. */}
      {(spec.to === 'closed' || spec.to === 'answered') &&
      resolution === LEGAL_TYPE_EXEMPT_RESOLUTION &&
      currentLegalType === undefined ? (
        <p className="text-xs text-ink-500">{t('admin.detail.legalTypeExempt')}</p>
      ) : null}

      {spec.needsAnswer ? (
        <Field
          label={t('admin.detail.answer')}
          hint={`${answerBody.trim().length} / ${ANSWER_BODY_MIN_CHARS}`}
        >
          <TextArea
            value={answerBody}
            minLength={ANSWER_BODY_MIN_CHARS}
            maxLength={8000}
            placeholder={t('admin.detail.answerPlaceholder')}
            onChange={(event) => setAnswerBody(event.target.value)}
          />
        </Field>
      ) : null}
    </Modal>
  );
}
